import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import CustomerLeadsRow from "./CustomerLeadsRow";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

let headRows = [
  {
    // The first column is for the expand/collapse button
    id: "expand_collapse",
    numeric: false,
    disablePadding: true,
    label: "",
    align: "center"
  },
  {
    id: "lead_date",
    numeric: true,
    disablePadding: true,
    label: "Lead Date",
    align: "left"
  },
  {
    id: "lead_name",
    numeric: false,
    disablePadding: true,
    label: "Parent Name",
    align: "left"
  },
  {
    id: "student_name",
    numeric: false,
    disablePadding: true,
    label: "Student Name",
    align: "left"
  },
  {
    id: "email",
    numeric: false,
    disablePadding: true,
    label: "email",
    align: "left"
  },
  {
    id: "lead_phone",
    numeric: true,
    disablePadding: false,
    label: "Phone",
    align: "left"
  },
  {
    id: "status",
    numeric: false,
    disablePadding: true,
    label: "Status",
    align: "center"
  },
  {
    id: "trial_date",
    numeric: false,
    disablePadding: true,
    label: "Trial Date",
    align: "center"
  },
];

function EnhancedCustomerLeadsTableHead(props) {
  const { order, orderBy, onRequestSort, showActions } = props;
  const createSortHandler = (property) => (e_leads) => {
    onRequestSort(e_leads, property);
  };

  const addAccessCols = () => {
    if (headRows[headRows.length - 1].id != "actions") {
      headRows = [
        ...headRows,
        {
          id: "actions",
          numeric: false,
          disablePadding: false,
          label: "Actions",
          align: "center"
        },
      ];
    }
  };
  if (showActions) {
    addAccessCols();
  }

  return (
    <TableHead>
      <TableRow>
        {headRows.map((row, index) => (
          <TableCell
            key={index}
            align={row.align}
            sortDirection={orderBy === row.id ? order : false}
          >
            {row.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedCustomerLeadsTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  regularClassIcon: {
    color: "#9f7431",
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

export default function EnhancedCustomerLeadsTable({
  leads,
  showActions,
  loading,
}) {
  const classes = useStyles();
  const rows = leads;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [accordianReset, setAccordianReset] = useState(false);

  function handleRequestSort(e_leads, property) {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  }

  function handleChangePage(e_leads, newPage) {
    setPage(newPage);
    setAccordianReset(true);
  }

  function handleChangeRowsPerPage(e_leads) {
    setRowsPerPage(+e_leads.target.value);
  }

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows?.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <EnhancedCustomerLeadsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows ? rows.length : 0}
              showActions={showActions}
            />
            {loading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress color="secondary" styles="width:100%" />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {rows &&
                  stableSort(rows, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <CustomerLeadsRow
                          key={row.lead_id}
                          row={row}
                          reset={accordianReset}
                          setReset={setAccordianReset}
                          showActions={showActions}
                        />
                      );
                    })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <TableCell colSpan={9} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows ? rows.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "Previous Page",
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page",
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
